/**
 * BundleDrawer class for handling bundle variant selection in drawer format
 * Extends HTMLElement and integrates drawer management functionality with bundle-specific features
 */
class BundleDrawer extends HTMLElement {
  #drawerId;
  #drawer = null;
  #overlay = null;
  #closeBtn = null;
  #previouslyFocusedElement = null;
  #originalParent = null;
  #originalNextSibling = null;
  #isTeleported = false;
  #transitionDuration = 300;

  #bundleData = {
    mainVariantId: null,
    quantity: 1,
    components: [],
    selectedVariants: new Map(),
    removedProducts: new Set(),
    totalPrice: 0,
  };

  #debouncedUpdatePrice;

  static FOCUSABLE_SELECTORS =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  static SELECTORS = {
    variantSelector: '.bundle-product__variant-selector',
    quantityInput: '.bundle-quantity .quantity__input',
    totalPrice: '.bundle-price-summary__price--total',
    addToCartBtn: '#BundleDrawer-add-to-cart',
    variantInput: 'input[name*="variant-id"]',
    removeBtn: '.bundle-product__remove',
    restoreBtn: '.bundle-product__restore',
    removedOverlay: '.bundle-product__removed-overlay',
    bundleProduct: '.bundle-product',
  };

  constructor() {
    super();
    this.#debouncedUpdatePrice = BundleDrawer.debounce(this.#updateTotalPrice, 150);
  }

  connectedCallback() {
    this.#drawerId = this.id || this.querySelector('.drawer')?.id || 'BundleDrawer';
    this.init();
  }

  init() {
    if (this.classList.contains('drawer') || this.classList.contains('drawer--bundle')) {
      this.#drawer = this;
    } else {
      this.#drawer = document.getElementById(this.#drawerId) || this.querySelector('.drawer');
    }

    if (!this.#drawer) {
      console.warn(`BundleDrawer: Drawer with ID "${this.#drawerId}" not found`);
      return;
    }

    this.drawerContent = this.#drawer.querySelector('[role="dialog"]') || this.#drawer;
    this.#overlay = this.#drawer.querySelector('.bundle-drawer__overlay');
    this.#closeBtn = this.#drawer.querySelector('[data-drawer-close]');
    this.#overlay?.addEventListener('click', this.close.bind(this));

    this.#initEventListeners();
    this.#initBundleEventListeners();
  }

  #initEventListeners() {
    this.#closeBtn?.addEventListener('click', this.#handleCloseClick);
    this.#overlay?.addEventListener('click', this.#handleOverlayClick);

    this.#drawer?.querySelectorAll('[data-drawer-close]').forEach((btn) => {
      if (btn !== this.#closeBtn) {
        btn.addEventListener('click', this.#handleCloseClick);
      }
    });
  }

  #initBundleEventListeners() {
    if (!this.#drawer) return;

    this.#drawer.querySelectorAll(BundleDrawer.SELECTORS.variantSelector).forEach((selector) => {
      selector.addEventListener('change', this.#handleVariantChange);
    });

    const quantityInput = this.#drawer.querySelector(BundleDrawer.SELECTORS.quantityInput);
    quantityInput?.addEventListener('change', this.#handleQuantityChange);

    const bundleForm = this.#drawer.querySelector(`#${this.#drawer.id}-Form`);
    bundleForm?.addEventListener('submit', this.#handleFormSubmit);

    this.#drawer.querySelectorAll(BundleDrawer.SELECTORS.removeBtn).forEach((removeBtn) => {
      removeBtn.addEventListener('click', this.#handleRemoveProduct);
    });
  }

  open() {
    if (!this.#drawer) return;

    this.#previouslyFocusedElement = document.activeElement;
    this.#teleportToBody();
    this.#setDrawerVisibility(false);

    this.#setPageInert(true);

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.#handleKeydown);

    requestAnimationFrame(() => {
      this.#drawer.classList.add('active');
      this.drawerContent?.classList.add('active');
      this.#focusFirstElement();

      this.#attachRestoreButtonListeners();
    });

    this.onOpen();
  }

  close() {
    if (!this.#drawer) return;

    document.removeEventListener('keydown', this.#handleKeydown);
    this.#drawer.classList.remove('active');
    this.drawerContent?.classList.remove('active');

    setTimeout(() => {
      this.#setDrawerVisibility(true);
      this.#restoreFromBody();
      this.#setPageInert(false);
      document.body.style.overflow = '';
      this.#restoreFocus();
    }, this.#transitionDuration);
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  isOpen() {
    return this.#drawer && !this.#drawer.hidden;
  }

  get drawer() {
    return this.#drawer;
  }

  #handleKeydown = (e) => {
    if (!this.isOpen()) return;

    if (e.key === 'Escape') {
      this.close();
    }
  };

  #handleOverlayClick = () => {
    this.close();
  };

  #handleCloseClick = () => {
    this.close();
  };

  #teleportToBody() {
    if (!this.#drawer || this.#isTeleported) return;

    this.#originalParent = this.#drawer.parentElement;
    this.#originalNextSibling = this.#drawer.nextElementSibling;

    document.body.appendChild(this.#drawer);
    this.#isTeleported = true;
  }

  #restoreFromBody() {
    if (!this.#drawer || !this.#isTeleported || !this.#originalParent) return;

    if (this.#originalNextSibling) {
      this.#originalParent.insertBefore(this.#drawer, this.#originalNextSibling);
    } else {
      this.#originalParent.appendChild(this.#drawer);
    }

    this.#isTeleported = false;
    this.#originalParent = null;
    this.#originalNextSibling = null;
  }

  #setDrawerVisibility(hidden) {
    this.#drawer.hidden = hidden;
    this.#drawer.setAttribute('aria-hidden', String(hidden));

    if (this.drawerContent && this.drawerContent !== this.#drawer) {
      this.drawerContent.hidden = hidden;
      this.drawerContent.setAttribute('aria-hidden', String(hidden));
    }
  }

  #focusFirstElement() {
    const closeButton = this.#drawer.querySelector('[data-drawer-close]');
    if (closeButton) {
      closeButton.focus();
      return;
    }

    this.#drawer.setAttribute('tabindex', '-1');
    this.#drawer.focus();
  }

  #restoreFocus() {
    if (this.#previouslyFocusedElement) {
      this.#previouslyFocusedElement.focus();
      this.#previouslyFocusedElement = null;
    }
  }

  #setPageInert(inert) {
    Array.from(document.body.children).forEach((child) => {
      if (child !== this.#drawer && child.tagName !== 'SCRIPT' && child.tagName !== 'STYLE') {
        child.inert = inert;
      }
    });
  }

  showLoading(button) {
    if (!button) return;

    const spinner = button.querySelector('.loading__spinner');
    const text = button.querySelector('[class*="text"]');

    if (spinner) {
      spinner.classList.remove('hidden');
      spinner.setAttribute('aria-hidden', 'false');
    }

    if (text) {
      text.style.opacity = '0';
    }

    button.disabled = true;
  }

  hideLoading(button) {
    if (!button) return;

    const spinner = button.querySelector('.loading__spinner');
    const text = button.querySelector('[class*="text"]');

    if (spinner) {
      spinner.classList.add('hidden');
      spinner.setAttribute('aria-hidden', 'true');
    }

    if (text) {
      text.style.opacity = '1';
    }

    button.disabled = false;
  }

  static debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  #handleRemoveProduct = (e) => {
    const removeBtn = e.target.closest(BundleDrawer.SELECTORS.removeBtn);
    if (!removeBtn) return;

    const bundleProduct = removeBtn.closest(BundleDrawer.SELECTORS.bundleProduct);
    if (!bundleProduct) return;

    const componentIndex = bundleProduct.dataset.componentIndex;
    if (componentIndex === undefined) return;
    this.#bundleData.removedProducts.add(componentIndex);

    const removedOverlay = bundleProduct.querySelector(BundleDrawer.SELECTORS.removedOverlay);
    if (removedOverlay) {
      removedOverlay.classList.remove('bundle-product__removed-overlay--hidden');
    }

    const variantSelector = bundleProduct.querySelector(BundleDrawer.SELECTORS.variantSelector);
    const variantInput = bundleProduct.querySelector(BundleDrawer.SELECTORS.variantInput);

    if (variantSelector) {
      variantSelector.removeAttribute('required');
      variantSelector.disabled = true;
    }

    if (variantInput) {
      variantInput.removeAttribute('required');
      variantInput.disabled = true;
    }

    removeBtn.disabled = true;
    removeBtn.setAttribute('tabindex', '-1');

    bundleProduct.classList.add('bundle-product--removed');
    this.#updateTotalPrice();
  };

  #handleRestoreProduct = (e) => {
    const restoreBtn = e.currentTarget;
    if (!restoreBtn || restoreBtn.tagName !== 'BUTTON') return;

    const bundleProduct = restoreBtn.closest(BundleDrawer.SELECTORS.bundleProduct);
    if (!bundleProduct) return;

    const componentIndex = bundleProduct.dataset.componentIndex;
    if (componentIndex === undefined) return;

    this.#bundleData.removedProducts.delete(componentIndex);

    const removedOverlay = bundleProduct.querySelector(BundleDrawer.SELECTORS.removedOverlay);
    if (removedOverlay) {
      removedOverlay.classList.add('bundle-product__removed-overlay--hidden');
    }

    const removeBtn = bundleProduct.querySelector(BundleDrawer.SELECTORS.removeBtn);
    if (removeBtn) {
      removeBtn.disabled = false;
      removeBtn.removeAttribute('tabindex');
    }

    // Re-enable form validation for restored products
    const variantSelector = bundleProduct.querySelector(BundleDrawer.SELECTORS.variantSelector);
    const variantInput = bundleProduct.querySelector(BundleDrawer.SELECTORS.variantInput);

    if (variantSelector) {
      variantSelector.disabled = false;
      const productId = bundleProduct.closest(BundleDrawer.SELECTORS.bundleProduct)?.dataset.productId;
      const originalSelector = productId && this.#drawer.querySelector(`select[data-product-id="${productId}"]`);
      originalSelector?.hasAttribute('form') && variantSelector.setAttribute('required', '');
    }

    if (variantInput) {
      variantInput.disabled = false;
    }

    bundleProduct.classList.remove('bundle-product--removed');
    this.#updateTotalPrice();
  };

  openBundle(mainVariantId, componentIds, quantity = 1) {
    const isDifferentBundle = this.#bundleData.mainVariantId !== mainVariantId;

    this.#bundleData.mainVariantId = mainVariantId;
    this.#bundleData.quantity = quantity;
    this.#bundleData.components = componentIds || [];
    this.#bundleData.selectedVariants.clear();

    isDifferentBundle && this.#bundleData.removedProducts.clear();

    this.#initializeSelectedVariants();
    this.#restoreRemovedProductsUI();

    this.#attachRestoreButtonListeners();

    this.open();
  }

  #attachRestoreButtonListeners() {
    const restoreDivs = this.#drawer.querySelectorAll('.bundle-product__restore');
    const restoreButtons = [];

    restoreDivs.forEach(div => {
      const button = div.closest('button');
      if (button && !restoreButtons.includes(button)) {
        restoreButtons.push(button);
      }
    });

    restoreButtons.forEach((restoreBtn) => {
      restoreBtn.removeEventListener('click', this.#handleRestoreProduct);
      restoreBtn.removeEventListener('keydown', this.#handleRestoreKeydown);

      restoreBtn.addEventListener('click', this.#handleRestoreProduct);
      restoreBtn.addEventListener('keydown', this.#handleRestoreKeydown);
    });
  }

  #handleRestoreKeydown = (e) => {
    if (e.key === 'Enter') {
      this.#handleRestoreProduct(e);
    }
  };

  #initializeSelectedVariants() {
    if (!this.#drawer) return;

    const variantSelectors = this.#drawer.querySelectorAll(
      `${BundleDrawer.SELECTORS.variantSelector}, ${BundleDrawer.SELECTORS.variantInput}`
    );

    variantSelectors.forEach((selector) => {
      const { componentIndex } = selector.dataset;
      const { variantId, price } = this.#extractVariantData(selector);

      if (variantId && componentIndex !== undefined) {
        this.#bundleData.selectedVariants.set(componentIndex, {
          variantId,
          price,
        });
      }
    });

    this.#updateTotalPrice();
  }

  #handleVariantChange = (e) => {
    const selector = e.target;
    const { componentIndex } = selector.dataset;

    const { variantId, price } = this.#extractVariantData(selector);

    this.#bundleData.selectedVariants.set(componentIndex, {
      variantId,
      price,
    });

    this.#debouncedUpdatePrice();
  };

  #extractVariantData(selector) {
    let variantId,
      price = 0;

    if (selector.tagName === 'SELECT') {
      const selectedOption = selector.options[selector.selectedIndex];
      variantId = selectedOption.value;
      price = parseFloat(selectedOption.dataset.price ?? '0');

      if (!variantId || variantId === '') {
        return { variantId: null, price: 0 };
      }
    } else if (selector.tagName === 'INPUT') {
      variantId = selector.value;
      price = parseFloat(selector.dataset.price ?? '0');
    }

    return { variantId, price };
  }

  #handleQuantityChange = (e) => {
    const newQuantity = parseInt(e.target.value, 10);
    if (newQuantity > 0) {
      this.#bundleData.quantity = newQuantity;
      this.#updateTotalPrice();
    }
  };

  #updateTotalPrice = () => {
    let total = 0;

    this.#bundleData.selectedVariants.forEach((variant, componentIndex) => {
      // Only include price if product is not removed
      if (!this.#bundleData.removedProducts.has(componentIndex)) {
        total += variant.price * this.#bundleData.quantity;
      }
    });

    this.#bundleData.totalPrice = total;
    this.#updateBundleSection();
    this.#updateAddToCartButton();
  };

  #updateAddToCartButton() {
    const addToCartBtn = this.#drawer.querySelector(BundleDrawer.SELECTORS.addToCartBtn);
    if (!addToCartBtn) return;

    const allBundleProducts = this.#drawer.querySelectorAll(BundleDrawer.SELECTORS.bundleProduct);
    const totalProducts = allBundleProducts.length;
    const removedCount = this.#bundleData.removedProducts.size;
    const hasActiveProducts = removedCount < totalProducts;

    addToCartBtn.disabled = !hasActiveProducts;
  }

  #handleFormSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      return;
    }

    this.#handleAddToCart();
  };

  async #handleAddToCart() {
    const addToCartBtn = this.#drawer.querySelector(BundleDrawer.SELECTORS.addToCartBtn);

    if (!addToCartBtn || addToCartBtn.disabled) {
      return;
    }

    this.showLoading(addToCartBtn);

    try {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const bundleItems = this.#prepareBundleItems();

      if (bundleItems.length === 0) {
        return;
      }

      const requestBody = {
        items: bundleItems,
      };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        const sectionsToRender = cart.getSectionsToRender();

        if (sectionsToRender && sectionsToRender.length > 0) {
          requestBody.sections = sectionsToRender.map((section) => section.section ?? section.id);
          requestBody.sections_url = window.location.pathname;
        }

        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Cart API error: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(
          `Server returned HTML instead of JSON. Response starts with: ${responseText.substring(0, 100)}`
        );
      }

      if (result.status) {
        throw new Error(result.description || result.message || 'Failed to add to cart');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-drawer',
          cartData: result,
        });
      }

      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(result);
        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      } else {
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        this.#updateCartIconBubble(cartData);

        if (cart && typeof cart.open === 'function') {
          cart.open();
        }
      }

      this.#resetBundleState();
      this.close();
    } finally {
      this.hideLoading(addToCartBtn);
    }
  }

  #prepareBundleItems() {
    return Array.from(this.#bundleData.selectedVariants.entries())
      .filter(
        ([componentIndex]) =>
          !this.#bundleData.removedProducts.has(componentIndex) &&
          !this.#bundleData.removedProducts.has(String(componentIndex))
      )
      .map(([componentIndex, variant]) => ({
        id: variant.variantId,
        quantity: this.#bundleData.quantity,
        properties: {
          _bundle: 'true',
          _bundle_id: `bundle_${Date.now()}`,
          _component_index: componentIndex,
        },
      }));
  }

  #updateBundleSection() {
    const totalPriceElement = this.#drawer.querySelector(BundleDrawer.SELECTORS.totalPrice);
    if (!totalPriceElement) return;

    if (window.theme && typeof window.theme.formatMoney === 'function') {
      const formattedPrice = window.theme.formatMoney(this.#bundleData.totalPrice);
      totalPriceElement.textContent = formattedPrice;
    }

    this.#updateIndividualProductPrices();
  }

  #updateIndividualProductPrices() {
    this.#bundleData.selectedVariants.forEach((variant, componentIndex) => {
      const priceElement = this.#drawer.querySelector(`#BundleDrawer-component-${componentIndex}-price`);

      if (priceElement && variant.price !== undefined) {
        priceElement.setAttribute('data-price', variant.price);
        if (window?.theme?.formatMoney) {
          const formattedPrice = window.theme.formatMoney(variant.price);
          priceElement.textContent = formattedPrice;
        }
      }
    });
  }

  onOpen() {
    this.#initializeSelectedVariants();
    this.#initializeQuantity();
  }

  #initializeQuantity() {
    const quantityInput = this.#drawer.querySelector(BundleDrawer.SELECTORS.quantityInput);
    if (quantityInput) {
      quantityInput.value = this.#bundleData.quantity;
    }
  }

  #updateCartIconBubble(cartData) {
    const cartIconBubble = document.querySelector('#cart-icon-bubble');
    if (!cartIconBubble) {
      return;
    }

    const cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
    const cartCountSpan = cartCountBubble?.querySelector('span[aria-hidden="true"]');

    cartData.item_count > 0
      ? cartCountSpan && cartData.item_count < 100 && (cartCountSpan.textContent = cartData.item_count)
      : cartCountBubble?.remove();
  }

  #restoreRemovedProductsUI() {
    if (!this.#drawer) return;

    this.#bundleData.removedProducts.forEach((componentIndex) => {
      const bundleProduct = this.#drawer.querySelector(`[data-component-index="${componentIndex}"]`);
      if (bundleProduct) {
        const removedOverlay = bundleProduct.querySelector(BundleDrawer.SELECTORS.removedOverlay);
        if (removedOverlay) {
          removedOverlay.classList.remove('bundle-product__removed-overlay--hidden');
        }
        bundleProduct.classList.add('bundle-product--removed');
      }
    });
  }

  #resetBundleState() {
    this.#bundleData.removedProducts.clear();
    this.#bundleData.quantity = 1;

    if (this.#drawer) {
      const allBundleProducts = this.#drawer.querySelectorAll(BundleDrawer.SELECTORS.bundleProduct);
      allBundleProducts.forEach((bundleProduct) => {
        bundleProduct.classList.remove('bundle-product--removed');

        const removedOverlay = bundleProduct.querySelector(BundleDrawer.SELECTORS.removedOverlay);
        if (removedOverlay) {
          removedOverlay.classList.add('bundle-product__removed-overlay--hidden');
        }

        const variantSelector = bundleProduct.querySelector(BundleDrawer.SELECTORS.variantSelector);
        const variantInput = bundleProduct.querySelector(BundleDrawer.SELECTORS.variantInput);
        const removeBtn = bundleProduct.querySelector(BundleDrawer.SELECTORS.removeBtn);

        if (variantSelector) {
          variantSelector.disabled = false;
          const productId = bundleProduct.dataset.productId;
          const originalSelector = productId && this.#drawer.querySelector(`select[data-product-id="${productId}"]`);
          if (originalSelector?.hasAttribute('form')) {
            variantSelector.setAttribute('required', '');
          }
        }

        if (variantInput) {
          variantInput.disabled = false;
        }

        if (removeBtn) {
          removeBtn.disabled = false;
          removeBtn.removeAttribute('tabindex');
        }
      });

      const quantityInput = this.#drawer.querySelector(BundleDrawer.SELECTORS.quantityInput);
      if (quantityInput) {
        quantityInput.value = 1;
      }
    }

    this.#updateTotalPrice();
  }
}

if (!customElements.get('bundle-drawer')) {
  customElements.define('bundle-drawer', BundleDrawer);
}

document.addEventListener('DOMContentLoaded', function () {
  const bundleDrawers = document.querySelectorAll('.drawer--bundle');

  bundleDrawers.forEach((drawer) => {
    if (!drawer.classList.contains('bundle-drawer') && drawer.tagName !== 'BUNDLE-DRAWER') {
      const drawerId = drawer.id;
      if (drawerId) {
        const instanceName = `bundleDrawer_${drawerId.replace(/-/g, '_')}`;
        const bundleElement = document.createElement('bundle-drawer');
        bundleElement.id = drawerId + '-wrapper';

        drawer.parentNode.insertBefore(bundleElement, drawer);
        bundleElement.appendChild(drawer);

        window[instanceName] = bundleElement;
      }
    }
  });
});

document.addEventListener('click', (e) => {
  const bundlePreviewBtn = e.target.closest('[data-bundle-preview]');
  if (!bundlePreviewBtn) return;
  e.preventDefault();

  document.dispatchEvent(new CustomEvent('bundle:open', {
    detail: {
      mainVariant: bundlePreviewBtn.dataset.mainVariant,
      components: bundlePreviewBtn.dataset.components.split(',').map(id => id.trim()).filter(Boolean),
      quantity: parseInt(bundlePreviewBtn.dataset.quantity) || 1
    }
  }));
});

document.addEventListener('bundle:open', (e) => {
  const bundleDrawer = document.querySelector('bundle-drawer');
  if (bundleDrawer && typeof bundleDrawer.openBundle === 'function') {
    bundleDrawer.openBundle(
      e.detail.mainVariant,
      e.detail.components,
      e.detail.quantity
    );
  }
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = BundleDrawer;
} else if (typeof window !== 'undefined') {
  window.BundleDrawer = BundleDrawer;
}

// "Configure" (bundle submit) button: scroll to the set builder instead of opening the drawer
document.addEventListener(
  'click',
  (e) => {
    const configureBtn = e.target.closest('button.product-form__submit.button--bundle');
    if (!configureBtn) return;

    const setBuilder = document.querySelector('maxton-set-builder');
    if (!setBuilder || setBuilder.hidden) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    setBuilder.scrollIntoView({ behavior: 'smooth', block: 'start' });
  },
  { capture: true },
);
